import { useEffect, useRef, useState, type FC } from "react";
import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { IconButton, Input, ModalClose } from "@racoon/ui";
import { IcCancel, IcProfile } from "@racoon/icons";
import { Mic, Plus, SendHorizontal, Smile } from "lucide-react";
import {
  useListWhatsappMessagesQuery,
  useSendWhatsappMessageMutation,
} from "~/shared/api/whatsapp/whatsapp.grpc";
import { WhatsappWallpaper } from "~/shared/assets/images";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";
import { useFreshJob } from "~/shared/hooks/use-fresh-job";
import { WhatsappMessageBubble } from "./whatsapp-dialogue.bubbles";

interface Props {
  job: ConnectProto.Job;
}

/**
 * The WhatsApp dialogue behind an order, drawn as the client's chat: contact header, the
 * transcript (client on the left, bot and dispatchers on the right) and a composer. It polls,
 * so the client's replies and delivery ticks show up live; the composer is open while
 * WhatsApp's 24-hour window after the client's last message is.
 */
export const WhatsappDialogueContent: FC<Props> = ({ job: openedWith }) => {
  const t = useT();
  // The modal keeps the job it was opened with; the summary card reads the backend's current one
  const job = useFreshJob(openedWith) ?? openedWith;
  // Polls every 3s while the chat is active (opened, a message came or went in the last
  // minute), every 15s once it goes quiet; each poll re-renders, so the switch is picked up
  const [activeAt, setActiveAt] = useState(() => Date.now());
  const { data, isLoading, isError } = useListWhatsappMessagesQuery(
    { jobId: job.id },
    {
      pollingInterval: Date.now() - activeAt < 60_000 ? 3000 : 15_000,
      skipPollingIfUnfocused: true,
    },
  );
  const messages = data?.messages ?? [];
  const contactName = messages.find((m) => m.contact?.name)?.contact?.name;
  const showMessages = !!data;
  useEffect(() => setActiveAt(Date.now()), [messages.length]);

  const [send] = useSendWhatsappMessageMutation();
  const [text, setText] = useState("");
  const canReply = !!data?.replyWindowEndsAt;
  // Another organization's zone job (or one not yet known as ours): its history only, no composer
  const isOwnForWrite = useIsOwnOrganizationForWrite("job");
  const canWrite = isOwnForWrite(job.serviceProviderId, job.serviceReceiverId) === true;
  const submit = async () => {
    const body = text.trim();
    if (!body) return;
    // The message shows at once (see sendWhatsappMessage); a failure is toasted globally
    // and puts the text back for another try
    setText("");
    const res = await send({ jobId: job.id, body, clientMessageId: crypto.randomUUID() });
    if ("error" in res) setText((current) => current || body);
  };

  // A chat opens on its latest message, and stays there when the composer appears under it
  const chatRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (showMessages && chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [showMessages, messages.length, canWrite]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-[72px] shrink-0 items-center gap-4 bg-white px-4">
        {/* No contact photo is stored yet: a placeholder stands in for it. IcProfile's circle
            is 16/20 of its box, so a 62.5px box draws the design's 50px avatar. */}
        <span className="flex size-[50px] shrink-0 items-center justify-center overflow-hidden rounded-full">
          <IcProfile className="size-[62.5px] shrink-0 text-slate-300" />
        </span>
        <span className="min-w-0 flex-1 truncate text-base font-medium text-text-body">
          {contactName ?? t(Namespace.Orders, "whatsappDialogue.title")}
        </span>
        <ModalClose asChild>
          <IconButton size="lg" aria-label={t(Namespace.Common, "close")}>
            <IcCancel className="text-text-primary" />
          </IconButton>
        </ModalClose>
      </div>

      {/* WhatsApp's doodle wallpaper; #F7F4EF is its base colour, shown until it loads */}
      <div
        ref={chatRef}
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-[#F7F4EF] bg-cover bg-center p-4 hidden-scrollbar"
        style={{ backgroundImage: `url(${WhatsappWallpaper})` }}
        data-testid="whatsapp-dialogue"
      >
        {isLoading &&
          [...Array(4)].map((_, i) => (
            <div
              key={i}
              className={`h-[72px] w-1/2 animate-pulse rounded-lg bg-white/70 ${i % 2 ? "self-end" : "self-start"}`}
            />
          ))}

        {!data && isError && (
          <div className="py-8 text-center text-sm text-text-subtle">
            {t(Namespace.Orders, "whatsappDialogue.error")}
          </div>
        )}

        {showMessages && messages.length === 0 && (
          <div className="py-8 text-center text-sm text-text-subtle">
            {t(Namespace.Orders, "whatsappDialogue.empty")}
          </div>
        )}

        {showMessages &&
          messages.map((msg) => (
            // A sent message keeps its key when the stored copy replaces the pending one
            <WhatsappMessageBubble
              key={msg.clientMessageId || String(msg.id)}
              msg={msg}
              job={job}
            />
          ))}
      </div>

      {canWrite && (
        <form
          className="flex h-16 shrink-0 items-center gap-2 bg-white px-4 text-text-subtle"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Smile className="size-6 shrink-0" aria-hidden />
          <Plus className="size-6 shrink-0" aria-hidden />
          <div className="flex h-8 min-w-0 flex-1 items-center rounded-full bg-slate-100 pr-4">
            <Input
              variant="ghost"
              radius="full"
              disabled={!canReply}
              value={text}
              maxLength={4096}
              onChange={(e) => setText(e.target.value)}
              placeholder={t(
                Namespace.Orders,
                canReply ? "whatsappDialogue.composerPlaceholder" : "whatsappDialogue.windowClosed",
              )}
              aria-label={t(Namespace.Orders, "whatsappDialogue.composerPlaceholder")}
              className="!h-8 !px-4 placeholder:!text-text-subtle hover:!bg-transparent focus-visible:!border-transparent focus-visible:!bg-transparent disabled:!opacity-100"
            />
            {text.trim() ? (
              <button
                type="submit"
                aria-label={t(Namespace.Orders, "whatsappDialogue.send")}
                className="shrink-0 text-text-information"
              >
                <SendHorizontal className="size-5" />
              </button>
            ) : (
              <Mic className="size-5 shrink-0" aria-hidden />
            )}
          </div>
        </form>
      )}
    </div>
  );
};
